import {
  LitheElement,
  LitheFC,
  LitheSpecificElement,
  redrawFragment,
} from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';

import LoadingIndicator from '@components/layout/loading-indicator';
import {
  DownloadIcon,
  CheckCircle2Icon,
  CircleIcon,
} from '@wheelbox/lithejs-lucide';

import { createId } from '@utils/index';
import {
  highlightCodeBlocks,
  isHighlighterReady,
} from '@lib/syntax-highlighter';
import {
  isSectionCompleted,
  toggleSectionCompletion,
} from '@lib/tutorial-progress';

import { LoadingIndicatorSize } from '@components/layout/loading-indicator/types';
import { type TutorialData, type TutorialMeta } from '@lib/tutorial-loader';

type TutorialContentViewProps = {
  data: TutorialData | null;
  sectionId?: string;
  previous?: TutorialMeta;
  next?: TutorialMeta;
  isLoading: boolean;
  classes?: string[] | string;
  onselect: (target: string) => void;
};

const TutorialContentView: LitheFC<TutorialContentViewProps> = ({
  id = createId(),
  testId = 'tutorial-content',
  data,
  sectionId,
  previous,
  next,
  isLoading,
  onselect,
}) => {
  const t = withTranslations('Tutorial');

  const onCodeDownloadHandler = (file: string) => {
    const target = document.createElement('a');
    target.setAttribute('href', `/code/${file}`);
    target.setAttribute('download', '');
    target.click();
    target.remove();
  };

  const renderTitle = (): LitheElement<'h1'> | null => {
    if (!data) return null;

    return [
      'h1',
      {
        class: 'text-3xl text-primary flex items-center mb-6',
      },
      data.title ?? '',
      ...(data.code
        ? [
            [
              'div',
              {
                class:
                  'flex items-center gap-2 text-sm font-normal text-muted-foreground ml-auto cursor-pointer hover:text-primary transition-colors',
                onclick: () => onCodeDownloadHandler(data.code!),
              },
              DownloadIcon({ size: 18, classes: 'h-4.5 w-4.5' }),
              t('get_code'),
            ] as LitheElement<'div'>,
          ]
        : []),
    ];
  };

  const renderContent = (): LitheSpecificElement => {
    if (!data?.content) {
      return ['h3', t('no_content')];
    }

    return [
      'div',
      {
        class: [
          'prose prose-slate dark:prose-invert max-w-none',
          // Headings
          '[&_h2]:text-2xl [&_h2]:text-primary [&_h2]:mt-8 [&_h2]:mb-4',
          '[&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3',
          // Code blocks
          '[&_pre]:bg-slate-900 [&_pre]:text-slate-50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:text-sm [&_pre]:leading-relaxed',
          '[&_pre]:border [&_pre]:border-slate-700',
          // Inline code
          '[&_code]:bg-primary/10 [&_code]:text-primary [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono',
          '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
          // Links
          '[&_a]:text-primary [&_a]:underline hover:[&_a]:text-primary/80',
          // Lists
          '[&_ul]:my-4 [&_ul]:pl-6 [&_li]:my-1',
          '[&_ol]:my-4 [&_ol]:pl-6',
          // Paragraphs
          '[&_p]:my-4 [&_p]:leading-7',
        ].join(' '),
      },
      data.content,
    ];
  };

  const renderCompletionButton = (): LitheSpecificElement | null => {
    if (!sectionId) return null;

    const handleToggle = () => {
      toggleSectionCompletion(sectionId);
      redrawFragment(`${id}-completion-button`);
      // Trigger sidebar refresh to update progress
      const event = new CustomEvent('tutorial-progress-changed');
      window.dispatchEvent(event);
    };

    const completed = isSectionCompleted(sectionId);
    return [
      'div',
      {
        id: `${id}-completion-button`,
        class: 'mt-8 pt-6 border-t',
      },
      [
        'button',
        {
          class: [
            'flex items-center gap-2 px-4 py-2 rounded-md transition-colors',
            completed
              ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
              : 'bg-muted hover:bg-muted/80 text-muted-foreground',
          ].join(' '),
          onclick: handleToggle,
        },
        completed
          ? CheckCircle2Icon({ size: 20, classes: 'text-green-500' })
          : CircleIcon({ size: 20 }),
        completed
          ? t('buttons.completed') || 'Completed'
          : t('buttons.markComplete') || 'Mark as complete',
      ],
    ];
  };

  const renderNavigation = (): LitheElement<'div'> | null => {
    if (!previous && !next) return null;

    return [
      'div',
      {
        class: [
          'flex flex-col md:flex-row mt-12 mb-4 gap-4',
          !previous ? 'justify-end' : 'justify-between',
        ],
      },
      ...(previous
        ? [
            [
              'button',
              {
                class:
                  'flex flex-col items-start px-4 py-2 rounded-md hover:bg-muted transition-colors',
                onclick: () => onselect(previous.filename),
              },
              [
                'span',
                { class: 'text-sm text-muted-foreground' },
                t('previous'),
              ],
              ['span', { class: 'text-primary' }, previous.title],
            ] as LitheElement<'button'>,
          ]
        : []),
      ...(next
        ? [
            [
              'button',
              {
                class:
                  'flex flex-col items-end px-4 py-2 rounded-md hover:bg-muted transition-colors ml-auto',
                onclick: () => onselect(next.filename),
              },
              ['span', { class: 'text-sm text-muted-foreground' }, t('next')],
              ['span', { class: 'text-primary' }, next.title],
            ] as LitheElement<'button'>,
          ]
        : []),
    ];
  };

  const renderLoading = () =>
    LoadingIndicator({ size: LoadingIndicatorSize.LARGE });

  const applyHighlighting = (element: HTMLElement) => {
    if (isHighlighterReady()) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        highlightCodeBlocks(element);
      });
    }
  };

  const renderLoaded = (): LitheSpecificElement[] => {
    const elements: LitheSpecificElement[] = [];

    const title = renderTitle();
    if (title) elements.push(title);

    elements.push(renderContent());

    // Use callback wrapper so redrawFragment can re-evaluate the state
    if (sectionId) {
      elements.push(() => renderCompletionButton()!);
    }

    const navigation = renderNavigation();
    if (navigation) elements.push(navigation);

    return elements;
  };

  return [
    'div',
    {
      id,
      'data-testid': testId,
      class: [
        'tutorial-content',
        isLoading
          ? 'flex min-h-[400px] w-full flex-col justify-center items-center'
          : '',
      ],
      onmount: (element: HTMLElement) => {
        if (!isLoading && data?.content) {
          applyHighlighting(element);
        }
      },
    },
    ...(isLoading ? [renderLoading()] : renderLoaded()),
  ];
};

export default TutorialContentView;
